import type {
  Content,
  ContentHref,
  ContentRelation,
  ContentRelationHref,
  ContentRelationKey,
  ContentRelationType,
  IsPartOfRelation,
} from '@generalTypes/apiTypes';
import { isKrcHorizontalRelation, isPrecedesRelation } from '@generalTypes/apiTypes';
import { getRelationsToAndFromMap } from '@newStore/documentApi/documentApiHelpers';
import {
  selectApiWithPendingChanges,
  selectApiWithPendingChangesRelationsToAndFromMap,
  selectBaselineRelationsToAndFromMap,
} from '@newStore/documentApi/documentApiSelectors';
import type { RelationsToAndFromMap } from '@newStore/documentApi/documentApiTypes';
import { getDiffForArraysOfStrings } from '@newStore/documentUI/transformProposal/asideDiffText';
import { getKrcExtendedIdentifierMap } from '@newStore/documentUI/transformContent/krcContentNodeSelectors';
import { getAllDescendantHrefs } from '@newStore/documentUI/documentUIHelpers';
import { getPathToRoot } from '@newStore/externalData/externalDataHelpers';
import { createTypedSelector, stripHtml } from '@newStore/genericHelpers';
import { krcCurriculumRoot } from '@newStore/referenceFrames/referenceFrames';
import {
  selectIsReferenceFrameLoaded,
  selectReferenceFrameContent,
} from '@newStore/referenceFrames/referenceFramesSelectors';
import type { RootState } from '@generalTypes/rootStateTypes';
import { escape as escapeHtml } from 'lodash';
import type {
  AsideChangeMessageSelector,
  EditAsideKrcGoalRelations,
  KrcGoalRelationUiType,
} from '@nodeTypeConfig/configTypes';
import {
  selectCurrentDocumentHref,
  selectRootHref,
} from '@newStore/documentUI/documentUISelectors';

type GoalContext = {
  disciplineHref: ContentHref | null;
  domainHref: ContentHref | null;
  subdomainHref: ContentHref | null;
};

export type KrcRelationStrength = 'LOW' | 'MEDIUM' | 'HIGH';

export type KrcGoalRelationOption = {
  href: ContentHref;
  disciplineHref: ContentHref | null;
  domainHref: ContentHref | null;
  identifier: string;
  title: string;
  label: string;
  searchValue: string;
};

export type KrcGoalRelationItem = {
  key: ContentRelationKey;
  relationType: KrcGoalRelationUiType;
  targetHref: ContentHref;
  disciplineHref: ContentHref | null;
  goalIdentifier: string;
  title: string;
  isLoaded: boolean;
};

const findHrefForType = (pathToRoot: Content[], type: string): ContentHref | null => {
  const item = pathToRoot.find((content) => content.type === type);
  return (item?.$$meta.permalink as ContentHref) || null;
};

const getGoalContext = (
  relationsMap: RelationsToAndFromMap,
  content: Record<ContentHref, Content>,
  goalHref: ContentHref,
): GoalContext => {
  const pathToRoot = getPathToRoot(relationsMap, content, goalHref);

  return {
    disciplineHref: findHrefForType(pathToRoot, 'KRC_CURRICULUM_DISCIPLINE'),
    domainHref: findHrefForType(pathToRoot, 'KRC_CURRICULUM_DOMAIN'),
    subdomainHref: findHrefForType(pathToRoot, 'KRC_CURRICULUM_SUBDOMAIN'),
  };
};

const getGoalTitle = (goal: Content | undefined): string => {
  return stripHtml(goal?.title) || 'Laden...';
};

const getGoalSearchValue = (identifier: string, title: string): string => {
  return `${identifier} ${title}`.trim();
};

const toGoalOption = (
  goal: Content | undefined,
  relationsMap: RelationsToAndFromMap,
  content: Record<ContentHref, Content>,
  identifierMap: Map<ContentHref, string>,
): KrcGoalRelationOption | null => {
  const href = goal?.$$meta?.permalink as ContentHref | undefined;
  if (!goal || !href) {
    return null;
  }

  const identifier = identifierMap.get(href) || '';
  const title = getGoalTitle(goal);
  const goalContext = getGoalContext(relationsMap, content, href);

  return {
    href,
    disciplineHref: goalContext.disciplineHref,
    domainHref: goalContext.domainHref,
    identifier,
    title,
    label: identifier ? `${identifier} - ${title}` : title,
    searchValue: getGoalSearchValue(identifier, title),
  };
};

export const getContentRelationTypeForKrcRelation = (
  relationType: KrcGoalRelationUiType,
): ContentRelationType => {
  if (relationType === 'PRECEDES_BEFORE' || relationType === 'PRECEDES_AFTER') {
    return 'PRECEDES';
  }

  return relationType;
};

export const isAlternativeKrcRelationType = (relationType: KrcGoalRelationUiType): boolean =>
  relationType === 'IS_ALTERNATIVE_FOR';

const createRelationItem = (
  goalOptionsMap: Map<ContentHref, KrcGoalRelationOption>,
  relation: {
    key: ContentRelationKey;
    targetHref: ContentHref;
    relationType: KrcGoalRelationUiType;
  },
  isReferenceFrameLoaded: boolean,
): KrcGoalRelationItem => {
  const goalOption = goalOptionsMap.get(relation.targetHref);
  const isMissing = !goalOption;
  return {
    key: relation.key,
    relationType: relation.relationType,
    targetHref: relation.targetHref,
    disciplineHref: goalOption?.disciplineHref ?? null,
    goalIdentifier: goalOption?.identifier || '',
    title: goalOption?.title || (isMissing ? 'Relatie niet gevonden' : ''),
    isLoaded: isReferenceFrameLoaded,
  };
};

const getKrcGoalRelationItems = (
  relationsMap: RelationsToAndFromMap,
  goalOptionsMap: Map<ContentHref, KrcGoalRelationOption>,
  currentGoalHref: ContentHref,
  isReferenceFrameLoaded: boolean,
): KrcGoalRelationItem[] => {
  const items: KrcGoalRelationItem[] = [];

  const incomingPrecedesRelations = (relationsMap.to[currentGoalHref] || []).filter(
    isPrecedesRelation,
  );
  const outgoingPrecedesRelations = (relationsMap.from[currentGoalHref] || []).filter(
    isPrecedesRelation,
  );

  incomingPrecedesRelations.forEach((relation) => {
    items.push(
      createRelationItem(
        goalOptionsMap,
        {
          key: relation.key,
          targetHref: relation.from.href as ContentHref,
          relationType: 'PRECEDES_BEFORE',
        },
        isReferenceFrameLoaded,
      ),
    );
  });

  outgoingPrecedesRelations.forEach((relation) => {
    items.push(
      createRelationItem(
        goalOptionsMap,
        {
          key: relation.key,
          targetHref: relation.to.href as ContentHref,
          relationType: 'PRECEDES_AFTER',
        },
        isReferenceFrameLoaded,
      ),
    );
  });

  const incomingHorizontalRelations = (relationsMap.to[currentGoalHref] || []).filter(
    isKrcHorizontalRelation,
  );
  const outgoingHorizontalRelations = (relationsMap.from[currentGoalHref] || []).filter(
    isKrcHorizontalRelation,
  );

  outgoingHorizontalRelations.forEach((relation) => {
    items.push(
      createRelationItem(
        goalOptionsMap,
        {
          key: relation.key,
          targetHref: relation.to.href as ContentHref,
          relationType: relation.relationtype,
        },
        isReferenceFrameLoaded,
      ),
    );
  });

  incomingHorizontalRelations.forEach((relation) => {
    items.push(
      createRelationItem(
        goalOptionsMap,
        {
          key: relation.key,
          targetHref: relation.from.href as ContentHref,
          relationType: relation.relationtype,
        },
        isReferenceFrameLoaded,
      ),
    );
  });

  return items;
};

const getChangeMessageLabels = (
  relationsMap: RelationsToAndFromMap,
  goalOptionsMap: Map<ContentHref, KrcGoalRelationOption>,
  href: ContentHref,
  relationType: KrcGoalRelationUiType,
): string[] => {
  return getKrcGoalRelationItems(relationsMap, goalOptionsMap, href, true)
    .filter((relation) => relation.relationType === relationType)
    .map((relation) => `${escapeHtml(relation.goalIdentifier)} - ${escapeHtml(relation.title)}`);
};

const toGoalOptionsMap = (
  options: KrcGoalRelationOption[],
): Map<ContentHref, KrcGoalRelationOption> => {
  const map = new Map<ContentHref, KrcGoalRelationOption>();
  for (const option of options) {
    map.set(option.href, option);
  }
  return map;
};

const selectReferenceFrameRawRelations = (state: RootState) =>
  state.referenceFrames[krcCurriculumRoot]?.relations as
    | Record<ContentRelationHref, ContentRelation>
    | undefined;

const selectMergedKrcContent = createTypedSelector(
  [
    (state) => selectApiWithPendingChanges(state).content,
    (state) => selectReferenceFrameContent(state, krcCurriculumRoot),
  ],
  (currentContent, referenceFrameContent) => {
    return { ...(referenceFrameContent || {}), ...currentContent };
  },
);

/**
 * Merges current document content+relations with the KRC reference frame and builds
 * a single RelationsToAndFromMap. This avoids the broken object-spread approach where
 * overlapping keys (e.g. krcCurriculumRoot) would lose the reference frame's children.
 */
const selectMergedKrcRelationsMap = createTypedSelector(
  [
    selectMergedKrcContent,
    (state) => selectApiWithPendingChanges(state).relationsMap,
    selectReferenceFrameRawRelations,
  ],
  (mergedContent, currentRelationsMap, referenceFrameRelations) => {
    // Both are Record<ContentRelationHref, ContentRelation>, so spread-merge deduplicates by key.
    // Current document relations take precedence (spread last).
    const mergedRelationsRecord = {
      ...(referenceFrameRelations || {}),
      ...currentRelationsMap,
    };

    return getRelationsToAndFromMap(mergedContent, Object.values(mergedRelationsRecord));
  },
);

/**
 * Selector that returns all KRC goals from both the current document and the KRC curriculum reference frame.
 * Goals from the current discipline come from the current document content (with pending changes),
 * while goals from other disciplines come from the reference frame.
 */
export const selectAllKrcGoalOptionsIncludingExternal = createTypedSelector(
  [
    selectMergedKrcContent,
    selectMergedKrcRelationsMap,
    (state) => selectCurrentDocumentHref(state),
    (state) => selectKrcGoalIdentifierMap(state),
  ],
  (mergedContent, mergedRelationsMap, currentDocumentHref, identifierMap) => {
    if (!currentDocumentHref) {
      return [];
    }

    // When editing the curriculum root itself, traverse from it directly;
    // otherwise traverse from the KRC root to get canonical ordering across all disciplines.
    const traversalRootHref = krcCurriculumRoot;

    const allChildHrefs = getAllDescendantHrefs(mergedRelationsMap.to, traversalRootHref);
    const allGoalHrefs = allChildHrefs.filter(
      (href) => mergedContent[href]?.type === 'KRC_CURRICULUM_GOAL',
    );

    const options: KrcGoalRelationOption[] = [];
    for (const href of allGoalHrefs) {
      const goal = mergedContent[href];
      const option = toGoalOption(goal, mergedRelationsMap, mergedContent, identifierMap);
      if (option) {
        options.push(option);
      }
    }

    return options;
  },
);

export const selectKrcCurrentGoalDomainHref = createTypedSelector(
  [
    (state) => selectApiWithPendingChanges(state).content,
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (state, href: ContentHref) => href,
  ],
  (content, relationsMap, href): ContentHref | null => {
    if (!content[href]) {
      return null;
    }

    return getGoalContext(relationsMap, content, href).domainHref;
  },
);

export const selectKrcGoalOptionsExcludingCurrent = createTypedSelector(
  [(state) => selectAllKrcGoalOptionsIncludingExternal(state), (state, href: ContentHref) => href],
  (options, currentGoalHref) => {
    return options.filter((option) => option.href !== currentGoalHref);
  },
);

export const selectAllKrcGoalRelations = createTypedSelector(
  [
    (state) => selectAllKrcGoalOptionsIncludingExternal(state),
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (state) =>
      selectRootHref(state) === krcCurriculumRoot ||
      selectIsReferenceFrameLoaded(state, krcCurriculumRoot),
    (state, href: ContentHref) => href,
  ],
  (goalOptions, relationsMap, isLoaded, href) => {
    return getKrcGoalRelationItems(relationsMap, toGoalOptionsMap(goalOptions), href, isLoaded);
  },
);

export const selectKrcGoalRelations = createTypedSelector(
  [
    (state, href: ContentHref) => selectAllKrcGoalRelations(state, href),
    (state, href: ContentHref, config: EditAsideKrcGoalRelations) => config,
  ],
  (relations, config) => {
    return relations.filter((relation) => relation.relationType === config.options.relationType);
  },
);

export const selectChangeMessageForKrcGoalRelations: AsideChangeMessageSelector<EditAsideKrcGoalRelations> =
  createTypedSelector(
    [
      (state) => selectAllKrcGoalOptionsIncludingExternal(state),
      (state) => selectBaselineRelationsToAndFromMap(state),
      (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
      (state, href: ContentHref) => href,
      (state, href: ContentHref, config: EditAsideKrcGoalRelations) => config,
    ],
    (goalOptions, rawRelationsMap, relationsMap, href, config): string | null => {
      const goalOptionsMap = toGoalOptionsMap(goalOptions);
      const before = getChangeMessageLabels(
        rawRelationsMap,
        goalOptionsMap,
        href,
        config.options.relationType,
      );
      const after = getChangeMessageLabels(
        relationsMap,
        goalOptionsMap,
        href,
        config.options.relationType,
      );
      return getDiffForArraysOfStrings(before, after);
    },
  );

/**
 * Context information about a KRC goal's ancestors (KRC_AGE_RANGE_ITEM and KRC_GOAL_SET_ITEM)
 * and their themes. Used by validations to avoid repeated parent-walk lookups.
 */
export type KrcGoalAncestorContext = {
  ageRangeItemHref: ContentHref | null;
  goalSetItemHref: ContentHref | null;
  ageRangeThemeHref: ContentHref | null;
  goalSetThemeHref: ContentHref | null;
};

const isPartOfRelation = (relation: ContentRelation | undefined): relation is IsPartOfRelation =>
  relation?.relationtype === 'IS_PART_OF';

const getDirectParentHref = (
  childHref: ContentHref,
  relationsMap: { from: Record<ContentHref, ContentRelation[] | undefined> },
): ContentHref | null => {
  const parentRelation = relationsMap.from[childHref]?.find(isPartOfRelation);
  return (parentRelation?.to.href as ContentHref) || null;
};

/**
 * Builds a Map keyed by every KRC_CURRICULUM_GOAL href (across the current document and the
 * merged KRC reference frame) containing its KRC_AGE_RANGE_ITEM and KRC_GOAL_SET_ITEM ancestor
 * hrefs and themes. This avoids re-walking the parent chain in validation rules that run for
 * each goal × relation × panel combination.
 */
export const selectKrcGoalAncestorContextMap = createTypedSelector(
  [selectMergedKrcContent, selectMergedKrcRelationsMap],
  (mergedContent, mergedRelationsMap): Map<ContentHref, KrcGoalAncestorContext> => {
    const contextMap = new Map<ContentHref, KrcGoalAncestorContext>();

    for (const key of Object.keys(mergedContent) as ContentHref[]) {
      const content = mergedContent[key];
      if (content?.type !== 'KRC_CURRICULUM_GOAL') continue;

      const goalHref = content.$$meta.permalink as ContentHref;
      const ageRangeItemHref = getDirectParentHref(goalHref, mergedRelationsMap);
      const goalSetItemHref = ageRangeItemHref
        ? getDirectParentHref(ageRangeItemHref, mergedRelationsMap)
        : null;

      contextMap.set(goalHref, {
        ageRangeItemHref,
        goalSetItemHref,
        ageRangeThemeHref:
          (ageRangeItemHref && (mergedContent[ageRangeItemHref]?.themes?.[0] ?? null)) || null,
        goalSetThemeHref:
          (goalSetItemHref && (mergedContent[goalSetItemHref]?.themes?.[0] ?? null)) || null,
      });
    }

    return contextMap;
  },
);

/**
 * Builds the forward PRECEDES graph for every KRC goal in the merged document + reference
 * frame, as a single shared lookup.
 *
 * Shape: `Map<fromHref, Set<toHref>>` — each entry is the set of direct successors of one
 * goal. An edge `X → Y` always means **"X precedes Y"** (Y comes after X). This direction is
 * the same regardless of whether the user is editing the BEFORE or AFTER panel, so callers
 * only ever need this one map to answer either question.
 *
 * Why we precompute it once:
 * - `selectMergedKrcRelationsMap` mixes every relation type (PRECEDES, IS_PART_OF,
 *   IS_HANDLED_TOGETHER, …). Filtering for PRECEDES on every BFS step is wasteful.
 * - Validation rules need transitive reachability (`is Y reachable from X by following
 *   PRECEDES?`). Once we have this successors map, that becomes a tight graph walk:
 *   `successorsMap.get(node)` → iterate successors → repeat.
 *
 * Example: with goals A, B, C and the PRECEDES relations `A → B`, `A → C`, `B → C`, the
 * resulting map is:
 *
 *   A → {B, C}
 *   B → {C}
 *
 * Using a Set per source means duplicate edges (same `from`/`to` appearing in both the
 * reference frame and the current document) are deduplicated automatically.
 */
export const selectKrcPrecedesSuccessorsMap = createTypedSelector(
  [selectMergedKrcRelationsMap],
  (mergedRelationsMap): Map<ContentHref, Set<ContentHref>> => {
    const successorsMap = new Map<ContentHref, Set<ContentHref>>();

    for (const fromHref of Object.keys(mergedRelationsMap.from) as ContentHref[]) {
      const outgoing = mergedRelationsMap.from[fromHref];
      if (!outgoing) continue;

      for (const relation of outgoing) {
        if (!isPrecedesRelation(relation)) continue;
        const toHref = relation.to.href as ContentHref;

        let successors = successorsMap.get(fromHref);
        if (!successors) {
          successors = new Set<ContentHref>();
          successorsMap.set(fromHref, successors);
        }
        successors.add(toHref);
      }
    }

    return successorsMap;
  },
);

/**
 * Map of every content href (merged document + KRC reference frame) to its extended
 * identifier (e.g. `4.3GL2.6`). Memoized so validation rules that need to print chains of
 * goals (including non-panel intermediates) don't have to recompute identifiers per render.
 */
export const selectKrcGoalIdentifierMap = createTypedSelector(
  [selectMergedKrcContent, selectMergedKrcRelationsMap],
  (mergedContent, mergedRelationsMap): Map<ContentHref, string> =>
    getKrcExtendedIdentifierMap(mergedRelationsMap, mergedContent),
);
